/**
 * useMergedHomeLayout — persists ONE ordered/sized list for the entire
 * Patient Home ("Today") grid.
 *
 * Storage:
 *   layout.patient_home_v2         : ordered ids
 *   layout.patient_home_v2__hidden : hidden ids
 *   layout.patient_home_v2__sizes  : "id:size" tokens
 *
 * Legacy migration (deterministic, non-destructive):
 *  - We NEVER overwrite an existing `patient_home_v2` row on load. Once a
 *    patient has customised the new grid, subsequent visits leave the row
 *    untouched.
 *  - When `patient_home_v2` is absent, we compute a default order derived
 *    from the three legacy rows (`patient_home_hero`, `patient_home_top`,
 *    `patient_home`) so the first visit shows the user's previously
 *    customised order. The `hero` id is expanded into `greeting`, `streak`,
 *    `sticker` (the three cards the old hero card contained).
 *  - Legacy rows are LEFT INTACT until the user actively saves a v2 change.
 *    Even after v2 is saved, we do not delete legacy rows — future rollbacks
 *    or other surfaces might still read them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token")}` });

const V2_KEY         = "patient_home_v2";
const V2_HIDDEN_KEY  = `${V2_KEY}__hidden`;
const V2_SIZES_KEY   = `${V2_KEY}__sizes`;

// iter111 · these four items are rendered OUTSIDE the grid as a fixed
// header. Strip them from any saved / migrated order so previously-customised
// layouts don't leave phantom cards inside the grid but also don't reset
// other cards' saved positions or sizes.
const HEADER_ONLY_IDS = new Set(["greeting", "sticker", "streak", "quote"]);
const stripHeader = (arr) => (arr || []).filter((id) => !HEADER_ONLY_IDS.has(id));
const stripHeaderSizes = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) if (!HEADER_ONLY_IDS.has(k)) out[k] = v;
  return out;
};

function mergeOrder(saved, def) {
  const defSet = new Set(def);
  const kept = (saved || []).filter((id) => defSet.has(id));
  const missing = def.filter((id) => !kept.includes(id));
  return [...kept, ...missing];
}

// Expand the legacy `hero` id (single card containing greeting + streak +
// sticker) into three independently-movable cards.
function expandHero(order) {
  const out = [];
  for (const id of order || []) {
    if (id === "hero") out.push("greeting", "streak", "sticker");
    else out.push(id);
  }
  return out;
}

function migrateLegacyOrder(layout, defaultOrder) {
  // Preserve the visible order from each legacy row, expanding `hero`, then
  // append anything from the defaults that wasn't already present.
  const hero = expandHero(layout[`patient_home_hero`] || []);
  const top = layout[`patient_home_top`] || [];
  const squares = layout[`patient_home`] || [];
  const merged = [...hero, ...top, ...squares];
  return mergeOrder(merged, defaultOrder);
}

function migrateLegacySizes(layout, defaultOrder) {
  const acc = {};
  const legacySizeKeys = [
    `patient_home_hero__sizes`,
    `patient_home_top__sizes`,
    `patient_home__sizes`,
  ];
  for (const k of legacySizeKeys) {
    for (const tok of layout[k] || []) {
      const [id, sz] = String(tok).split(":");
      if (id && sz && defaultOrder.includes(id)) acc[id] = sz;
    }
  }
  return acc;
}

export function useMergedHomeLayout(defaultOrder, defaultSizes = {}) {
  const [order, setOrderState]   = useState(defaultOrder);
  const [hidden, setHiddenState] = useState(() => new Set());
  const [sizes, setSizesState]   = useState({ ...defaultSizes });
  const [ready, setReady]        = useState(false);
  // Set to true once a legacy migration has been rendered so the FIRST
  // persist writes it into the v2 row (rather than leaving the migration
  // visible only in the current session's memory).
  const migratedFromLegacy = useRef(false);
  const toastCooldown = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/dashboard-layout`, { headers: H() });
        if (cancelled) return;
        const layout = data?.layout || {};
        const hasV2 = Array.isArray(layout[V2_KEY]) && layout[V2_KEY].length > 0;
        if (hasV2) {
          setOrderState(stripHeader(mergeOrder(layout[V2_KEY], defaultOrder)));
          setHiddenState(new Set(stripHeader((layout[V2_HIDDEN_KEY] || []).filter((id) => defaultOrder.includes(id)))));
          const sizeAcc = { ...defaultSizes };
          for (const tok of layout[V2_SIZES_KEY] || []) {
            const [id, sz] = String(tok).split(":");
            if (id && sz && defaultOrder.includes(id) && !HEADER_ONLY_IDS.has(id)) sizeAcc[id] = sz;
          }
          setSizesState(sizeAcc);
        } else {
          // First visit — migrate legacy rows into a virtual v2 order.
          const migrated = stripHeader(migrateLegacyOrder(layout, defaultOrder));
          const migratedSizes = stripHeaderSizes({ ...defaultSizes, ...migrateLegacySizes(layout, defaultOrder) });
          setOrderState(migrated);
          setSizesState(migratedSizes);
          migratedFromLegacy.current = true;
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setReady(true); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback(async (patch, options = {}) => {
    try {
      // Read-modify-write pattern so we ONLY touch v2 keys; legacy rows and
      // other surfaces (clinician_home, patient_progress_*) stay intact.
      const { data } = await axios.get(`${API}/me/dashboard-layout`, { headers: H() });
      const layout = { ...(data?.layout || {}) };
      // Safety gate: never overwrite an already-customised v2 with legacy
      // migration output. Once the user has any v2 row, we treat it as canon.
      const serverHasV2 = Array.isArray(layout[V2_KEY]) && layout[V2_KEY].length > 0;
      if (serverHasV2 && migratedFromLegacy.current && !patch.userInitiated) {
        migratedFromLegacy.current = false;
        return; // do nothing — server already has customised layout
      }
      if (patch.order)  layout[V2_KEY]        = patch.order;
      if (patch.hidden) layout[V2_HIDDEN_KEY] = [...patch.hidden];
      if (patch.sizes)  layout[V2_SIZES_KEY]  = Object.entries(patch.sizes).map(([k, v]) => `${k}:${v}`);
      if (patch.clear) {
        delete layout[V2_KEY];
        delete layout[V2_HIDDEN_KEY];
        delete layout[V2_SIZES_KEY];
      }
      await axios.put(`${API}/me/dashboard-layout`, { layout }, { headers: H() });
      migratedFromLegacy.current = false;
      const now = Date.now();
      if (!options.silent && now - toastCooldown.current > 4000) {
        toastCooldown.current = now;
        toast.success("Layout saved — synced to your other devices");
      }
    } catch { /* offline / auth blip — keep optimistic state */ }
  }, []);

  const setOrder = useCallback((next) => {
    setOrderState(next);
    persist({ order: next, userInitiated: true });
  }, [persist]);

  const toggleHidden = useCallback((id) => {
    setHiddenState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persist({ hidden: next, userInitiated: true });
      return next;
    });
  }, [persist]);

  const setSize = useCallback((id, size) => {
    setSizesState((prev) => {
      const next = { ...prev, [id]: size };
      persist({ sizes: next, userInitiated: true });
      return next;
    });
  }, [persist]);

  const resetToDefault = useCallback(() => {
    setOrderState(defaultOrder);
    setHiddenState(new Set());
    setSizesState({ ...defaultSizes });
    persist({ clear: true, userInitiated: true }, { silent: true });
    toast.success("Layout reset to default");
  }, [defaultOrder, defaultSizes, persist]);

  const visibleOrder = useMemo(() => order.filter((id) => !hidden.has(id)), [order, hidden]);

  return {
    order, visibleOrder, hidden, sizes,
    setOrder, toggleHidden, setSize, resetToDefault,
    ready,
  };
}
